import {
  dashboardSectionCardClassName,
  dashboardSectionCardContentClassName,
} from "@/modules/dashboard/presentation/ui/dashboard-card-styles";
import { CompactMoneyAmount } from "@/shared/ui/CompactMoneyAmount";
import { Card, CardContent, cn, Skeleton } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import { ChevronRightIcon, StarIcon } from "lucide-react";
import { useMemo } from "react";
import { usePerformanceReportSummaryQuery } from "../api/queries";
import { formatCurrencyBuckets } from "./PerformanceReportsNav";

type PerformanceMainDashboardWidgetProps = {
  dateFrom: string;
  dateTo: string;
};

export function PerformanceMainDashboardWidget({
  dateFrom,
  dateTo,
}: PerformanceMainDashboardWidgetProps) {
  const query = useMemo(
    () => ({ dateFrom, dateTo, groupBy: "day" as const }),
    [dateFrom, dateTo],
  );

  const { data, isLoading } = usePerformanceReportSummaryQuery(query);
  const payoutOutstanding = data?.payoutOutstanding ?? 0;

  return (
    <Link
      to="/app/performance/reports"
      search={{ dateFrom, dateTo }}
      className="group block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Card
        className={cn(
          dashboardSectionCardClassName,
          "overflow-hidden transition-colors group-hover:border-primary/35 group-hover:bg-primary/[0.02]",
        )}
      >
        <CardContent className={cn(dashboardSectionCardContentClassName, "p-0")}>
          <div className="flex items-center justify-between gap-3 border-b px-5 py-3.5 sm:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <StarIcon className="size-5" aria-hidden />
              </div>
              <p className="font-medium text-sm leading-snug">
                ລາຍຮັບງານສະແດງ / ຄ້າງຈ່າຍ
              </p>
            </div>
            <ChevronRightIcon
              className="size-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
              aria-hidden
            />
          </div>

          <div className="grid sm:grid-cols-2 sm:divide-x">
            <div className="space-y-2 border-b px-5 py-4 sm:border-b-0 sm:px-6 sm:py-5">
              <p className="text-muted-foreground text-xs">ລາຍຮັບເກັບແລ້ວ</p>
              {isLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-7 w-36" />
                  <Skeleton className="h-5 w-24" />
                </div>
              ) : (
                formatCurrencyBuckets(data?.revenueCollected, {
                  showEmpty: true,
                  className: "font-semibold text-xl tabular-nums tracking-tight sm:text-2xl",
                })
              )}
            </div>

            <div className="space-y-2 px-5 py-4 sm:px-6 sm:py-5">
              <p className="text-muted-foreground text-xs">ຄ້າງຈ່າຍນັກສະແດງ</p>
              {isLoading ? (
                <Skeleton className="h-9 w-32" />
              ) : (
                <CompactMoneyAmount
                  amount={payoutOutstanding}
                  className="font-semibold text-xl sm:text-2xl"
                  currencyClassName="font-medium text-base sm:text-lg"
                />
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
